/**
 * The Fortinet menu of the vertical navigation.
 *
 * While a Fortinet page is open, the application shell
 * (dcnetscope-main.component) shows this menu instead of its own: the device
 * scope (a switcher over "All devices" and every firewall), then the
 * sections of whatever is selected, then the groups of the fleet page.
 *
 * Entries, badges and device list all come from the Fortinet page through
 * FortinetNavService.state$; this component only displays them and sends
 * back what the user picked (FortinetNavService.send).
 *
 * The file also holds FortinetDevicesService, which tells the shell whether
 * any FortiGate has data, to gray out the Fortinet tab when none has.
 */
import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef, Component, ElementRef, HostListener, Injectable, OnDestroy, OnInit,
} from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  DeviceTab, FORTINET_FLEET_VIEW, FortinetNavService, NAV_EMPTY, NavDevice, NavEntry,
  NavGroup, NavSev, NavState, compareGroupsAsTree, groupAndParents, groupDepth,
  groupLeafName, groupPathText, mainGroupOf,
} from './fortinet-shared';

/* ============================================================================
 * Are there Fortinet devices?
 * ========================================================================== */

@Injectable({ providedIn: 'root' })
export class FortinetDevicesService {
  constructor(private http: HttpClient) {}

  /** Emits once: false when no FortiGate has written any data yet, true
   *  otherwise. It asks devices.json, which only lists the devices (the
   *  fleet route would build the document of every device on each page
   *  load). The API answers 404 with a JSON message when there is no device
   *  (see netscope/web/fortinet_blueprint.py). Any other failure (API
   *  unreachable, session expired, an older API without devices.json...)
   *  gives true: the Fortinet tab stays usable, and the Fortinet page
   *  explains the error itself. */
  hasDevices(apiBase = environment.fortinetApiUrl): Observable<boolean> {
    const request = this.http.get(`${apiBase}/devices.json`);
    const answered = request.pipe(map(answerMeansDevices));
    return answered.pipe(catchError(errorMeansDevices));
  }
}

/** The devices route answered: there is at least one device. */
function answerMeansDevices(): boolean {
  return true;
}

/** The devices route failed: only the API's own "No FortiGate data yet" (a
 *  404 with a JSON message) means "no device". A 404 page without that
 *  message is a route the API doesn't have (older API): not a "no". */
function errorMeansDevices(error: unknown): Observable<boolean> {
  let noDevice = false;
  if (error instanceof HttpErrorResponse && error.status === 404) {
    const body = error.error;
    noDevice = !!body && typeof body === 'object' && 'message' in body;
  }
  return of(!noDevice);
}

/* ============================================================================
 * Device picker
 * ========================================================================== */

/** One line of the device picker: a group heading (that opens and closes)
 *  or a device. */
interface PickerRow {
  kind: 'group' | 'device';
  /** unique id of the line, for trackBy: 'g:' + group path, or 'd:' + serial */
  key: string;
  /** 0 for a top group, 1 for what is inside it... (to indent the line) */
  depth: number;
  /** group: its last level ("Paris"); device: its name and serial */
  label: string;
  /** device found by a search: its groups ("France › Paris, Lab"); otherwise '' */
  sub: string;
  /** group: the devices inside it and inside the groups below it */
  count: number;
  /** device: its state; group: the worst state of the devices inside it */
  sev: NavSev;
  /** group only: its devices and sub-groups are shown */
  open: boolean;
  /** group only: the full path ("France/Paris"), '' for "No group" */
  groupPath: string;
  /** device only */
  serial: string;
}

/** A group of the picker while it is being built (see buildPicker). */
interface PickerNode {
  path: string;
  /** devices whose MAIN group is exactly this group */
  devices: NavDevice[];
  /** devices in this group and in the groups below it */
  count: number;
  worstSev: NavSev;
  holdsSelectedDevice: boolean;
}

/** From how many devices the groups of the picker start closed. With fewer
 *  devices, everything is shown open. */
const PICKER_COLLAPSE_FROM = 9;

/** Where the browser remembers which groups of the picker the user opened or
 *  closed: {"France": true, "France/Paris": false}. Only a convenience: the
 *  picker works the same without it. */
const PICKER_STORAGE_KEY = 'fgPickerGroupState';

/** Below this window width (in px) the menu starts collapsed, so the page
 *  keeps most of a small screen. The user can still open it. */
const COLLAPSE_MENU_BELOW = 992;

/** Width of the device picker when the menu is collapsed (the button is then
 *  too narrow to give it its width). */
const PICKER_MIN_WIDTH = 260;

/** Free space kept between the picker and the bottom of the window. */
const PICKER_BOTTOM_MARGIN = 16;

/** Device states from the worst to the best, to find the worst one of a group. */
const SEV_RANK: Record<NavSev, number> = { danger: 0, offline: 1, warning: 2, unknown: 3, ok: 4 };

/** The devices needing attention first, then by name. */
function compareDevices(first: NavDevice, second: NavDevice): number {
  const bySev = SEV_RANK[first.sev] - SEV_RANK[second.sev];
  if (bySev !== 0) {
    return bySev;
  }
  return first.text.localeCompare(second.text);
}

/* ============================================================================
 * Component
 * ========================================================================== */

@Component({
  selector: 'app-fortinet-nav',
  standalone: true,
  imports: [CommonModule, ClarityModule],
  templateUrl: './fortinet-nav.component.html',
  styleUrls: ['./fortinet-nav.component.css'],
})
export class FortinetNavComponent implements OnInit, OnDestroy {
  nav: NavState = NAV_EMPTY;
  readonly fleetId = FORTINET_FLEET_VIEW;
  pickerOpen = false;
  pickerFilter = '';
  pickerRows: PickerRow[] = [];
  /** the picker shows a tree of groups (not a search result, not a flat list) */
  pickerHasTree = false;
  /** where the picker is drawn on the screen, in px (see placePicker) */
  pickerTop = 0;
  pickerLeft = 0;
  pickerWidth = PICKER_MIN_WIDTH;
  pickerMaxHeight = 400;
  /** the menu shows only its icons */
  navCollapsed = window.innerWidth < COLLAPSE_MENU_BELOW;
  /** id of the picker, for aria-controls of its button */
  readonly pickerId = 'fg-picker-' + Math.random().toString(36).slice(2, 8);
  /** the switcher button, to give it the focus back when Escape closes the picker */
  private switchButton: HTMLElement | null = null;

  /** the groups the user opened (true) or closed (false) by hand, by path */
  private groupChoices: Record<string, boolean> = this.readGroupChoices();
  /** the groups of the last build of the picker, for "Expand all" / "Collapse all" */
  private pickerGroupPaths: string[] = [];
  private navSubscription: Subscription;
  /** true once Angular removed the menu (leaving the Fortinet page) */
  private destroyed = false;

  constructor(
    private fortinetNav: FortinetNavService,
    private ref: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.navSubscription = this.fortinetNav.state$.subscribe((state) => this.onNavState(state));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.navSubscription?.unsubscribe();
  }

  private onNavState(state: NavState): void {
    this.nav = state;
    if (!state.active) {
      this.pickerOpen = false;
      this.pickerFilter = '';
    }
    this.buildPicker();
    // Out of the current change-detection pass: the Fortinet page
    // publishes its state while it renders. The last state is published
    // as the page closes, which also removes this menu: detectChanges on a
    // removed view throws an error, hence the check.
    setTimeout(() => this.redrawUnlessDestroyed());
  }

  private redrawUnlessDestroyed(): void {
    if (!this.destroyed) {
      this.ref.detectChanges();
    }
  }

  /** The entries under the switcher: fleet sections, or device tabs. */
  get sections(): NavEntry[] {
    if (this.nav.fleetMode) {
      return this.nav.fleetSections;
    }
    return this.nav.sections;
  }

  togglePicker(switchButton: HTMLElement): void {
    this.switchButton = switchButton;
    this.pickerOpen = !this.pickerOpen;
    if (this.pickerOpen) {
      this.pickerFilter = '';
      this.placePicker(switchButton);
      this.buildPicker();
      // The keyboard focus goes into the search box, so the user can type
      // at once (the box only exists after the next drawing, hence the timeout).
      setTimeout(() => this.focusPickerSearch());
    }
  }

  private focusPickerSearch(): void {
    const searchBox = this.host.nativeElement.querySelector<HTMLInputElement>('.fg-picker-filter');
    searchBox?.focus();
  }

  /** Puts the picker right under the switcher button, in window coordinates
   *  (the picker is position: fixed). Collapsed, the button is only an icon,
   *  so the picker gets a minimum width and goes out of the menu to the
   *  right. Its height stops above the bottom of the window. */
  private placePicker(switchButton: HTMLElement): void {
    const box = switchButton.getBoundingClientRect();
    this.pickerTop = box.bottom + 2;
    this.pickerLeft = box.left;
    this.pickerWidth = Math.max(box.width, PICKER_MIN_WIDTH);
    this.pickerMaxHeight = window.innerHeight - this.pickerTop - PICKER_BOTTOM_MARGIN;
  }

  /** Clarity's own button collapsed or opened the menu. */
  onCollapsedChange(collapsed: boolean): void {
    this.navCollapsed = collapsed;
    // the button moved: the picker would float where it was
    this.pickerOpen = false;
  }

  /** Opens the collapsed menu (the folded "Groups" entry). */
  expandMenu(): void {
    this.navCollapsed = false;
  }

  /** Tooltip of the folded "Groups" entry of the collapsed menu. */
  get foldedGroupsText(): string {
    const filter = this.nav.groupFilter;
    if (filter === null) {
      return `Groups (${this.nav.groups.length}): open the menu to pick one`;
    }
    const groupName = filter === '' ? 'the devices without a group' : groupPathText(filter);
    return `Page focused on ${groupName}: open the menu to change`;
  }

  setPickerFilter(text: string): void {
    this.pickerFilter = text;
    this.buildPicker();
  }

  /** Builds the lines of the device picker (pickerRows). Three cases:
   *  - something is typed in the search box: a flat list of the matching
   *    devices, each with its groups under it, so a device is found whatever
   *    group it is in;
   *  - no device has a group: a flat list of every device;
   *  - otherwise: a tree of groups. Each device is shown once, under its MAIN
   *    group, and a group can be opened and closed (see groupIsOpen).
   *  Inside a group, the devices needing attention come first. */
  private buildPicker(): void {
    const query = this.pickerFilter.trim().toLowerCase();
    const allDevices = this.nav.devices;
    this.pickerRows = [];
    this.pickerGroupPaths = [];
    this.pickerHasTree = false;

    // 1. search: flat list of the matching devices (the search looks in every group)
    if (query !== '') {
      const matches: NavDevice[] = [];
      for (const device of allDevices) {
        const searchedText = `${device.text} ${device.groups.join(' ')}`.toLowerCase();
        if (searchedText.includes(query)) {
          matches.push(device);
        }
      }
      for (const device of this.sortDevices(matches)) {
        const groupTexts: string[] = [];
        for (const group of device.groups) {
          groupTexts.push(groupPathText(group));
        }
        this.pickerRows.push(this.deviceRow(device, 0, groupTexts.join(', ')));
      }
      return;
    }

    // 2. no group at all: flat list
    let someDeviceHasAGroup = false;
    for (const device of allDevices) {
      if (device.groups.length > 0) {
        someDeviceHasAGroup = true;
      }
    }
    if (!someDeviceHasAGroup) {
      for (const device of this.sortDevices(allDevices)) {
        this.pickerRows.push(this.deviceRow(device, 0, ''));
      }
      return;
    }

    // 3. tree. First, one node per main group and per group above it:
    // a device in "France/Paris" is counted in "France" and in "France/Paris",
    // and listed under "France/Paris". A device without a group goes in ''.
    this.pickerHasTree = true;
    const selectedSerial = this.nav.fleetMode ? null : this.nav.selectedSerial;
    const nodes = new Map<string, PickerNode>();
    for (const device of allDevices) {
      const mainGroup = mainGroupOf(device.groups);
      const paths = mainGroup === '' ? [''] : groupAndParents(mainGroup);
      for (const path of paths) {
        let node = nodes.get(path);
        if (!node) {
          node = { path, devices: [], count: 0, worstSev: 'ok', holdsSelectedDevice: false };
          nodes.set(path, node);
        }
        node.count++;
        if (SEV_RANK[device.sev] < SEV_RANK[node.worstSev]) {
          node.worstSev = device.sev;
        }
        if (device.serial === selectedSerial) {
          node.holdsSelectedDevice = true;
        }
      }
      // the last path is the main group itself
      const mainNode = nodes.get(paths[paths.length - 1]);
      mainNode.devices.push(device);
    }

    // Then the lines, each group right before what is inside it, "No group"
    // last. A group is only shown when every group above it is open.
    const paths = [...nodes.keys()];
    paths.sort(compareGroupsAsTree);
    this.pickerGroupPaths = paths;
    for (const path of paths) {
      const node = nodes.get(path);
      if (!this.parentsAreOpen(path, nodes)) {
        continue;
      }
      const open = this.groupIsOpen(node);
      const depth = path === '' ? 0 : groupDepth(path);
      this.pickerRows.push({
        kind: 'group',
        key: 'g:' + path,
        depth,
        label: path === '' ? 'No group' : groupLeafName(path),
        sub: '',
        count: node.count,
        sev: node.worstSev,
        open,
        groupPath: path,
        serial: '',
      });
      if (open) {
        for (const device of this.sortDevices(node.devices)) {
          this.pickerRows.push(this.deviceRow(device, depth + 1, ''));
        }
      }
    }
  }

  /** Is this group of the picker open? The user's own choice wins (it is
   *  remembered by the browser). Without one, the group starts open when:
   *  - there are only a few devices in all, or
   *  - it holds the device being shown, or
   *  - it holds a device in a critical or offline state. */
  private groupIsOpen(node: PickerNode): boolean {
    const choice = this.groupChoices[node.path];
    if (choice !== undefined) {
      return choice;
    }
    if (this.nav.devices.length < PICKER_COLLAPSE_FROM) {
      return true;
    }
    if (node.holdsSelectedDevice) {
      return true;
    }
    return node.worstSev === 'danger' || node.worstSev === 'offline';
  }

  /** Are all the groups above this one open? ("France" for "France/Paris") */
  private parentsAreOpen(path: string, nodes: Map<string, PickerNode>): boolean {
    if (path === '') {
      return true;
    }
    const groupAndItsParents = groupAndParents(path);
    // every item but the last one (the group itself)
    for (let index = 0; index < groupAndItsParents.length - 1; index++) {
      const parent = nodes.get(groupAndItsParents[index]);
      if (parent && !this.groupIsOpen(parent)) {
        return false;
      }
    }
    return true;
  }

  /** A sorted copy: the devices needing attention first, then by name. */
  private sortDevices(devices: NavDevice[]): NavDevice[] {
    const sorted = [...devices];
    sorted.sort(compareDevices);
    return sorted;
  }

  private deviceRow(device: NavDevice, depth: number, sub: string): PickerRow {
    return {
      kind: 'device', key: 'd:' + device.serial, depth, label: device.text, sub,
      count: 0, sev: device.sev, open: false, groupPath: '', serial: device.serial,
    };
  }

  /** A click on a group heading of the picker: opens or closes it, and
   *  remembers it. */
  toggleGroup(row: PickerRow): void {
    this.groupChoices[row.groupPath] = !row.open;
    this.saveGroupChoices();
    this.buildPicker();
  }

  /** "Expand all" (true) and "Collapse all" (false) of the picker. */
  setAllGroupsOpen(open: boolean): void {
    for (const path of this.pickerGroupPaths) {
      this.groupChoices[path] = open;
    }
    this.saveGroupChoices();
    this.buildPicker();
  }

  /** The open/closed groups remembered by this browser. Browser storage can
   *  be missing or blocked (private window): then nothing is remembered. */
  private readGroupChoices(): Record<string, boolean> {
    try {
      const saved = localStorage.getItem(PICKER_STORAGE_KEY);
      if (!saved) {
        return {};
      }
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (error) {
      // nothing remembered: every group gets its default state
    }
    return {};
  }

  private saveGroupChoices(): void {
    try {
      localStorage.setItem(PICKER_STORAGE_KEY, JSON.stringify(this.groupChoices));
    } catch (error) {
      // storage blocked: the choice only lasts until the page is reloaded
    }
  }

  /* ---- what the user picked, sent to the Fortinet page ------------------- */

  selectDevice(serial: string): void {
    this.pickerOpen = false;
    this.fortinetNav.send({ kind: 'device', serial });
  }

  selectSection(entry: NavEntry): void {
    if (entry.off) {
      return;
    }
    if (this.nav.fleetMode) {
      this.fortinetNav.send({ kind: 'section', id: entry.id });
    } else {
      this.fortinetNav.send({ kind: 'tab', tab: entry.id as DeviceTab });
    }
  }

  selectGroup(name: string): void {
    this.fortinetNav.send({ kind: 'group', name });
  }

  /* ---- trackBy of the lists ------------------------------------------------ */

  trackRow(_index: number, row: PickerRow): string {
    return row.key;
  }

  trackSection(_index: number, section: NavEntry): string {
    return section.id;
  }

  trackGroup(_index: number, group: NavGroup): string {
    return group.name;
  }

  /* ---- closing the picker ---------------------------------------------------- */

  /** A click anywhere else closes the device picker. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.pickerOpen) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target || !target.closest('.fg-scope')) {
      this.pickerOpen = false;
      this.ref.detectChanges();
    }
  }

  /** The picker is placed in window coordinates: when the window changes
   *  size, the switcher moves and the picker would stay behind. */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.pickerOpen) {
      this.pickerOpen = false;
      this.ref.detectChanges();
    }
  }

  /** Escape closes the picker, and gives the focus back to its button (the
   *  focus was inside the picker, which disappears). */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.pickerOpen) {
      this.pickerOpen = false;
      this.ref.detectChanges();
      this.switchButton?.focus();
    }
  }
}
